import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Download,
  Description,
  PictureAsPdf,
  Image,
  InsertDriveFile,
  CloudUpload,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

interface ClubDocument {
  id: string;
  club_id: string;
  title: string;
  description: string | null;
  file_name: string;
  file_path: string;
  file_type: string | null;
  file_size: number | null;
  category: string | null;
  is_public: boolean;
  display_order: number;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

interface ClubDocumentsManagerProps {
  clubId: string;
}

const CATEGORIES = ['regulations', 'forms', 'minutes', 'reports', 'safety', 'other'];

const ClubDocumentsManager: React.FC<ClubDocumentsManagerProps> = ({ clubId }) => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<ClubDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<ClubDocument | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<ClubDocument | null>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFormData, setUploadFormData] = useState({
    title: '',
    description: '',
    category: '',
    is_public: false,
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: '',
    is_public: false,
  });

  useEffect(() => {
    if (clubId) {
      fetchDocuments();
    }
  }, [clubId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/clubs/${clubId}/documents`);
      setDocuments(response.data.items || []);
    } catch (err: any) {
      console.error('Failed to fetch documents:', err);
      setError(t('clubDocuments.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadFormData.title) {
        setUploadFormData({
          ...uploadFormData,
          title: file.name.replace(/\.[^/.]+$/, ''),
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    try {
      setSaving(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadFormData.title);
      formData.append('description', uploadFormData.description);
      formData.append('category', uploadFormData.category);
      formData.append('is_public', uploadFormData.is_public.toString());

      await api.post(`/clubs/${clubId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(t('common.savedSuccessfully'));
      setTimeout(() => setSuccess(null), 3000);
      handleCloseUploadDialog();
      fetchDocuments();
    } catch (err: any) {
      console.error('Failed to upload document:', err);
      setError(t('clubDocuments.errors.uploadFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setUploadFormData({
      title: '',
      description: '',
      category: '',
      is_public: false,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenEditDialog = (document: ClubDocument) => {
    setEditingDocument(document);
    setEditFormData({
      title: document.title,
      description: document.description || '',
      category: document.category || '',
      is_public: document.is_public,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingDocument(null);
    setEditFormData({
      title: '',
      description: '',
      category: '',
      is_public: false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingDocument) return;

    try {
      setSaving(true);
      setError(null);

      await api.put(`/clubs/${clubId}/documents/${editingDocument.id}`, editFormData);

      setSuccess(t('common.savedSuccessfully'));
      setTimeout(() => setSuccess(null), 3000);
      handleCloseEditDialog();
      fetchDocuments();
    } catch (err: any) {
      console.error('Failed to save document:', err);
      setError(t('clubDocuments.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (document: ClubDocument) => {
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      setSaving(true);
      await api.delete(`/clubs/${clubId}/documents/${documentToDelete.id}`);
      setSuccess(t('common.deletedSuccessfully'));
      setTimeout(() => setSuccess(null), 3000);
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
      fetchDocuments();
    } catch (err: any) {
      console.error('Failed to delete document:', err);
      setError(t('clubDocuments.errors.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (document: ClubDocument) => {
    try {
      const response = await api.get(`/clubs/${clubId}/documents/${document.id}/download`);
      if (response.data.download_url) {
        // Construct full URL using the API base URL
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
        const baseUrl = apiBaseUrl.replace('/api/v1', '');
        const fullUrl = `${baseUrl}${response.data.download_url}`;
        window.open(fullUrl, '_blank');
      }
    } catch (err: any) {
      console.error('Failed to download document:', err);
      setError(t('clubDocuments.errors.downloadFailed'));
    }
  };

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return <InsertDriveFile />;
    if (fileType.includes('pdf')) return <PictureAsPdf color="error" />;
    if (fileType.includes('image')) return <Image color="primary" />;
    return <Description color="action" />;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">{t('clubDocuments.title')}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setUploadDialogOpen(true)}
        >
          {t('clubDocuments.addDocument')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Description sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {t('clubDocuments.noDocuments')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('clubDocuments.noDocumentsDescription')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            {t('clubDocuments.addDocument')}
          </Button>
        </Paper>
      ) : (
        <List>
          {documents.map((document) => (
            <Paper key={document.id} sx={{ mb: 1 }}>
              <ListItem>
                <ListItemIcon>
                  {getFileIcon(document.file_type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {document.title}
                      </Typography>
                      {document.category && (
                        <Chip
                          label={t(`clubDocuments.categories.${document.category}`)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {document.is_public && (
                        <Chip
                          label={t('clubDocuments.isPublic')}
                          size="small"
                          color="info"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {document.file_name}
                      </Typography>
                      {document.file_size && (
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(document.file_size)}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(document.created_at)}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="download"
                    onClick={() => handleDownload(document)}
                    sx={{ mr: 1 }}
                  >
                    <Download />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => handleOpenEditDialog(document)}
                    sx={{ mr: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeleteClick(document)}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('clubDocuments.addDocument')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
              >
                {uploadFile ? uploadFile.name : t('clubDocuments.selectFile')}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('clubDocuments.maxFileSize')} - {t('clubDocuments.allowedTypes')}
              </Typography>
            </Box>
            <TextField
              fullWidth
              required
              label={t('clubDocuments.documentTitle')}
              value={uploadFormData.title}
              onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('clubDocuments.description')}
              value={uploadFormData.description}
              onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>{t('clubDocuments.category')}</InputLabel>
              <Select
                value={uploadFormData.category}
                label={t('clubDocuments.category')}
                onChange={(e) => setUploadFormData({ ...uploadFormData, category: e.target.value })}
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {t(`clubDocuments.categories.${cat}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadFormData.is_public}
                  onChange={(e) => setUploadFormData({ ...uploadFormData, is_public: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('clubDocuments.isPublic')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('clubDocuments.isPublicHelper')}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>{t('clubDocuments.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={saving || !uploadFile || !uploadFormData.title}
          >
            {saving ? t('clubDocuments.uploading') : t('clubDocuments.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('clubDocuments.editDocument')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              required
              label={t('clubDocuments.documentTitle')}
              value={editFormData.title}
              onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('clubDocuments.description')}
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>{t('clubDocuments.category')}</InputLabel>
              <Select
                value={editFormData.category}
                label={t('clubDocuments.category')}
                onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {t(`clubDocuments.categories.${cat}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editFormData.is_public}
                  onChange={(e) => setEditFormData({ ...editFormData, is_public: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('clubDocuments.isPublic')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('clubDocuments.isPublicHelper')}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{t('clubDocuments.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving || !editFormData.title}
          >
            {saving ? t('clubDocuments.saving') : t('clubDocuments.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('clubDocuments.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('clubDocuments.deleteConfirmMessage')}</Typography>
          {documentToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {documentToDelete.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('clubDocuments.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={saving}
          >
            {t('clubDocuments.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClubDocumentsManager;
